import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Edit,
  ArrowLeft,
  Users,
  Briefcase,
  Clock,
  User,
  Archive,
  MapPinIcon,
  FolderIcon,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { EditEmployeeSheet } from "./EditEmployeeSheet";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";

interface Employee {
  _id: Id<"employees">;
  name: string;
  avatar?: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  hireDate: string;
  status: "active" | "inactive";
  createdAt: number;
}

export default function EmployeeView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  // Validate ID parameter
  const isValidId = id && id !== "undefined" && id.trim() !== "";

  // Fetch employee data from Convex
  const employee = useQuery(
    api.employees.getEmployeeById,
    isValidId ? { id: id as Id<"employees"> } : "skip"
  );

  // Fetch employee service locations
  const employeeServiceLocations = useQuery(
    api.employees.getEmployeeServiceLocations,
    isValidId ? { employeeId: id as Id<"employees"> } : "skip"
  );

  // Fetch employee projects
  const employeeProjects = useQuery(
    api.employees.getEmployeeProjects,
    isValidId ? { employeeId: id as Id<"employees"> } : "skip"
  );

  // Archive mutation
  const archiveEmployee = useMutation(api.employees.archive);

  // Handle archive employee
  const handleArchive = async () => {
    if (!employee) return;

    try {
      await archiveEmployee({ id: employee._id });
      toast.success(`${employee.name} has been archived successfully`);
      navigate("/employees");
    } catch (error) {
      console.error("Failed to archive employee:", error);
      toast.error("Failed to archive employee");
    }
  };

  // Handle invalid ID
  if (!isValidId) {
    return (
      <DashboardLayout activeTab="company-profile">
        <div className="flex h-full w-full items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold">Invalid Employee ID</h2>
            <p className="mb-4 text-gray-500">The employee ID provided is invalid.</p>
            <Button onClick={() => navigate("/employees")}>Back to Employees</Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Handle loading state
  if (employee === undefined) {
    return (
      <DashboardLayout activeTab="company-profile">
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-700"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Handle employee not found
  if (employee === null) {
    return (
      <DashboardLayout activeTab="company-profile">
        <div className="flex h-full w-full items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold">Employee Not Found</h2>
            <p className="mb-4 text-gray-500">
              The employee you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate("/employees")}>Back to Employees</Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="company-profile">
      <div className="h-full w-full space-y-6">
        <div className="mb-4 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => navigate("/employees")}
          >
            <ArrowLeft className="h-4 w-4" /> Back to Employees
          </Button>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={employee.avatar} alt={employee.name} />
              <AvatarFallback className="bg-blue-100 text-xl font-medium text-blue-600">
                {employee.name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">{employee.name}</h1>
                <Badge
                  className={`${employee.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"}`}
                >
                  {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                </Badge>
              </div>
              <div className="mt-1 flex items-center gap-2 text-gray-500">
                <Briefcase className="h-4 w-4" />
                <span>{employee.position}</span>
              </div>
              <div className="mt-1 text-sm text-gray-500">
                Hired: {new Date(employee.hireDate).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={handleArchive}>
              <Archive className="h-4 w-4" /> Archive
            </Button>
            <Button className="gap-2" onClick={() => setIsEditSheetOpen(true)}>
              <Edit className="h-4 w-4" /> Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Left Column - Details */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{employee.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{employee.phone}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Department</h2>
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="h-4 w-4" />
                <span>{employee.department}</span>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Employment Details</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>Hire Date: {new Date(employee.hireDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>Status: {employee.status}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Tabs */}
          <div className="md:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="service-locations" className="gap-2">
                  <MapPinIcon className="h-4 w-4" />
                  Service Locations
                </TabsTrigger>
                <TabsTrigger value="projects" className="gap-2">
                  <FolderIcon className="h-4 w-4" />
                  Projects
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 text-lg font-semibold">Employee Overview</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="mb-2 font-medium text-gray-900">Position</h4>
                      <p className="text-gray-600">{employee.position}</p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-gray-900">Department</h4>
                      <p className="text-gray-600">{employee.department}</p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-gray-900">Employment Status</h4>
                      <Badge
                        className={`${employee.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"}`}
                      >
                        {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="service-locations" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                    <MapPinIcon className="h-5 w-5" />
                    Service Locations
                  </h3>
                  {employeeServiceLocations === undefined ? (
                    <div className="py-8 text-center text-gray-500">
                      Loading service locations...
                    </div>
                  ) : employeeServiceLocations.length === 0 ? (
                    <EmptyState
                      icon={MapPinIcon}
                      title="No service locations"
                      description="No service locations assigned to this employee."
                    />
                  ) : (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {employeeServiceLocations.map((assignment: any) => (
                          <Card key={assignment._id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">
                                    {assignment.location.name}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    {assignment.business.name}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {assignment.role}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                <div>
                                  <span className="font-medium text-gray-700">Shift Schedule:</span>
                                  <p className="text-gray-600">{assignment.shift}</p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Location Address:
                                  </span>
                                  <p className="text-gray-600">{assignment.location.address}</p>
                                </div>
                                {assignment.location.squareFootage && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Square Footage:
                                    </span>
                                    <p className="text-gray-600">
                                      {assignment.location.squareFootage.toLocaleString()} sq ft
                                    </p>
                                  </div>
                                )}
                                {assignment.location.serviceFrequency && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Service Frequency:
                                    </span>
                                    <p className="text-gray-600">
                                      {assignment.location.serviceFrequency}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {assignment.location.serviceDays &&
                                assignment.location.serviceDays.length > 0 && (
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">
                                      Service Days:
                                    </span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {assignment.location.serviceDays.map((day: any) => (
                                        <Badge key={day} variant="secondary" className="text-xs">
                                          {day}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              <div className="border-t border-gray-100 pt-2">
                                <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                  {assignment.business.contactName && (
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Client Contact:
                                      </span>
                                      <p className="text-gray-600">
                                        {assignment.business.contactName}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <span className="font-medium text-gray-700">Assigned:</span>
                                    <p className="text-gray-600">
                                      {new Date(assignment.assignedAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="projects" className="space-y-6">
                <Card className="p-6">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                    <FolderIcon className="h-5 w-5" />
                    Projects
                  </h3>
                  {employeeProjects === undefined ? (
                    <div className="py-8 text-center text-gray-500">Loading projects...</div>
                  ) : employeeProjects.length === 0 ? (
                    <EmptyState
                      icon={FolderIcon}
                      title="No projects"
                      description="No projects assigned to this employee."
                    />
                  ) : (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {employeeProjects.map((project: any) => (
                          <Card key={project._id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{project.name}</h4>
                                  <p className="text-sm text-gray-600">
                                    {project.location.name} - {project.business.name}
                                  </p>
                                </div>
                                <Badge
                                  variant={
                                    project.status === "Completed"
                                      ? "default"
                                      : project.status === "In Progress"
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className={
                                    project.status === "Completed"
                                      ? "bg-green-100 text-green-800"
                                      : project.status === "In Progress"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-yellow-100 text-yellow-800"
                                  }
                                >
                                  {project.status}
                                </Badge>
                              </div>

                              {project.description && (
                                <p className="text-sm text-gray-600">{project.description}</p>
                              )}

                              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                <div>
                                  <span className="font-medium text-gray-700">Start Date:</span>
                                  <p className="text-gray-600">
                                    {new Date(project.startDate).toLocaleDateString()}
                                  </p>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Due Date:</span>
                                  <p className="text-gray-600">
                                    {new Date(project.dueDate).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>

                              <div>
                                <span className="text-sm font-medium text-gray-700">
                                  Team Members:
                                </span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {project.assignedEmployeeNames.map((name: any, index: any) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              {project.assignedSubcontractorNames.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    Subcontractors:
                                  </span>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {project.assignedSubcontractorNames.map(
                                      (name: any, index: any) => (
                                        <Badge key={index} variant="outline" className="text-xs">
                                          {name}
                                        </Badge>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="border-t border-gray-100 pt-2">
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium text-gray-700">Location:</span>{" "}
                                  {project.location.address}
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <EditEmployeeSheet
        isOpen={isEditSheetOpen}
        onClose={() => setIsEditSheetOpen(false)}
        employee={employee}
        onSave={() => {
          // Optionally refresh data or show success message
          setIsEditSheetOpen(false);
        }}
      />
    </DashboardLayout>
  );
}
